import React from 'react'
import { motion } from 'framer-motion'
import { StaticImage } from 'gatsby-plugin-image'
import { FiZap, FiMail, FiFileText, FiCheckCircle } from 'react-icons/fi'

const OneClickGenerator = () => {
  const containerVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { staggerChildren: 0.3 } }
  }

  const itemVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 }
  }

  const pulseVariants = {
    initial: { scale: 1 },
    animate: { 
      scale: [1, 1.05, 1],
      transition: { duration: 2, repeat: Infinity }
    }
  }

  const documentTypes = [
    { 
      icon: <FiFileText className="w-full h-full" />, 
      title: "Tailored Resume", 
      desc: "Keywords matched to job requirements" 
    },
    { 
      icon: <FiFileText className="w-full h-full" />, 
      title: "Custom Cover Letter", 
      desc: "Aligned with job qualifications" 
    },
    { 
      icon: <FiMail className="w-full h-full" />, 
      title: "Follow-up Email", 
      desc: "Matched to company culture" 
    }
  ]

  return (
    <div className="relative bg-white py-16 overflow-hidden">
      <motion.div 
        variants={containerVariants}
        initial="initial"
        animate="animate"
        className="container mx-auto px-4"
      >
        <motion.div variants={itemVariants} className="text-center mb-24">
          <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
            <FiZap className="mr-2" /> AI-Powered Job Matching
          </span>
          <h2 className="mt-4 text-4xl font-bold text-neutral-900 mb-8">
            Generate Tailored Documents with
            <span className="text-indigo-600"> One Click</span>
          </h2>
          
          <div className="max-w-xl mx-auto mb-8">
            <textarea 
              placeholder="Paste your job description here..."
              className="w-full p-4 border border-indigo-100 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              rows={4}
            />
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.open('https://app.resumeflex.com/auth', '_blank')}
            className="relative px-8 py-4 bg-indigo-600 text-white rounded-xl font-medium text-sm flex items-center mx-auto shadow-xl group"
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-xl"
              animate={{
                background: ['rgba(79, 70, 229, 1)', 'rgba(67, 56, 202, 1)'],
              }}
              transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse' }}
            />
            <span className="relative flex items-center">
              Generate Tailored Documents 
              <FiZap className="ml-2 group-hover:animate-pulse" />
            </span>
          </motion.button>

          <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-16 bg-gradient-to-b from-indigo-600 to-transparent mt-4">
            <motion.div
              className="absolute top-0 left-0 w-full h-full bg-indigo-400"
              animate={{
                opacity: [0, 1, 0],
                y: [0, 100, 0],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>

        <div className="relative max-w-5xl mx-auto">
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full h-full">
            <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <motion.path
                d="M50,0 L20,50 L80,50 L50,0"
                fill="none"
                stroke="url(#gradient)"
                strokeWidth="0.5"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#4F46E5" />
                  <stop offset="100%" stopColor="#818CF8" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {documentTypes.map((item, index) => (
              <motion.div
                key={index}
                variants={pulseVariants}
                className="relative bg-white p-6 rounded-xl shadow-lg border border-indigo-100 z-10"
              >
                <div className="absolute -top-3 -left-3 bg-indigo-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">
                  {index + 1}
                </div>
                <div className="text-indigo-600 w-8 h-8 mb-4">
                  {item.icon}
                </div>
                <h3 className="text-sm font-semibold mb-2">{item.title}</h3>
                <p className="text-xs text-neutral-600">{item.desc}</p>
                <motion.div
                  className="absolute bottom-0 left-0 w-full h-1 bg-indigo-600/20"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default OneClickGenerator
